import memoize from 'lodash/memoize'
import * as ListDataManager from 'dataContainer/listDataManager'

/**
 * Does the transform logic for
 * @param valueObj
 * @param rowObj
 * @param templateField
 * @param templateFields
 * @param trigger
 * @returns {null|*}
 */
export function transformDrilldownListValue(
  valueObj,
  rowObj,
  templateField,
  templateFields,
  context,
  dataContainer,
  readOnlyTransform,
) {
  // set auto update to false by default
  valueObj.metadata = valueObj.metadata || {}

  // as implemented, default value template fields always are hidden, so they can't be changed.
  // as such, they should always have the default value if they don't have a value
  // this causes some weird UI interactions if unhidden (can't delete), but since they always
  // are hidden, it's fine for now.  We need to determine if default value mean "always set to this"
  // or "assume this until it's changed"
  if (!valueObj.cell_value && templateField.default_value) {
    valueObj.cell_value = templateField.default_value
  }

  const templateFieldsByFieldId = templateFields.reduce((memo, tf) => {
    memo[tf.field_id] = tf
    return memo
  }, {})

  // update the valid options metadata
  updateValidOptions(valueObj, rowObj, templateField, templateFieldsByFieldId)

  // Must be called after updateValidOptions are set.
  const isQualifiedForAutofillOfSingleItem =
    !readOnlyTransform && !valueObj.cell_value && (context.last_deleted || 0) + 1000 < Date.now()
  if (isQualifiedForAutofillOfSingleItem) {
    autofillIfSingleItem(valueObj, templateField, context)
  }

  if (!readOnlyTransform) {
    // clear if the option is invalid
    // must be called after options are updated
    handleInvalidValues(valueObj, templateField, context)

    // clear if predecessors aren't set
    clearValueIfUnmetPredecessors(valueObj, rowObj, templateField, templateFieldsByFieldId)

    // update readOnly state
    updateReadOnlyState(valueObj, rowObj, templateField, templateFieldsByFieldId)
  }

  if (context.isResubmit && valueObj.cell_value !== valueObj.originalValue && !readOnlyTransform) {
    clearAllOriginalValuesOnAnyListFieldChangee(valueObj, templateField, context, dataContainer)
  }

  determineFieldDefaultValuesAndVisibility({
    valueObj,
    templateField,
    templateFieldsByFieldId,
    rowObj,
    dataContainer,
  })

  // return the cell value
  if (valueObj.cell_value) {
    // If list is multiselect make sure values are sorted
    const listGovernance = getListGovernance(templateField)
    const { predecessors } = listGovernance.meta
    const delimiterHolder = predecessors[0]
      ? templateFieldsByFieldId[predecessors[0]]
      : templateField

    const multi =
      delimiterHolder.field.list_governance && !!delimiterHolder.field.list_governance.multi
    if (multi) {
      const { delimiter } = delimiterHolder.field.list_governance
      valueObj.cell_value = valueObj.cell_value.split(delimiter).sort().join(delimiter)
    }
  }

  return valueObj.cell_value
}

/**
 * Determines the default values and visibility of the field.
 *
 * @param valueObj
 * @param templateField
 * @param templateFieldsByFieldId
 * @param rowObj
 * @param dataContainer
 * @returns void
 */

function determineFieldDefaultValuesAndVisibility({
  valueObj,
  templateField,
  templateFieldsByFieldId,
  rowObj,
  dataContainer,
}) {
  const validOptions =
    valueObj?.metadata?.validOptions ||
    getPossibleValues(rowObj, templateField, templateFieldsByFieldId) ||
    []

  const currentValueIsValid = validOptions.includes(valueObj.cell_value)
  if (currentValueIsValid) return

  const isHidden = Boolean(!valueObj?.metadata?.isVisible)
  const isPoweredList = Boolean(templateField?.field?.list_governance?.lists?.powered_template_id)
  const noDefaultValue = Boolean(!templateField?.default_value)
  const hasDefaultValue = Boolean(templateField?.default_value)
  const fieldWasntUpdatedYet = !dataContainer?.fieldsUpdatedAfterLoad?.includes(
    templateField.field_id,
  )
  const fieldIsNotVisible = !templateField?.visible_on_form
  const hasValidationErros = valueObj?.metadata?.validationErrors?.length > 0

  if (isHidden && isPoweredList && noDefaultValue) {
    /**
     * If the field is hidden and is a powered list, and has no default value, we
     * need to set the value to the first valid option.
     */
    valueObj.cell_value = validOptions[0]
  } else if (!isPoweredList && hasDefaultValue) {
    /**
     * If the field is not a powered list and has a default value, we need to set the value to the default value.
     */
    valueObj.cell_value = templateField?.default_value
  } else if (
    !isPoweredList &&
    noDefaultValue &&
    fieldIsNotVisible &&
    fieldWasntUpdatedYet &&
    hasValidationErros
  ) {
    /**
     * If the field is not a powered list, has no default value, has no valid options,
     * is not visible on the form, wasn't updated yet and has validation errors,
     * we need to show it, so the user can fix the error.
     */
    dataContainer.changeFieldVisibility(templateField.field_id, true)
  }
}

/**
 *
 * @param valueObj
 * @param rowObj
 * @param templateField
 */
function clearValueIfUnmetPredecessors(valueObj, rowObj, templateField, templateFields) {
  // if it's already cleared, don't bother doing the reduce
  if (!valueObj.cell_value) {
    return
  }

  // get the predecessors
  const listGovernance = getListGovernance(templateField)
  const predecessors = listGovernance.meta.predecessors || []

  // determine if predecessors have been filled
  const hasUnmetPredecessors = predecessors.reduce((memo, fieldId) => {
    const valueHolder = rowObj[fieldId]
    let value = (valueHolder || {}).cell_value
    value = templateFields[fieldId].default_value ? templateFields[fieldId].default_value : value
    return memo || !value
  }, false)

  // if predecessors are unfilled, then clear this value.  It can't exist
  if (hasUnmetPredecessors) {
    valueObj.cell_value = null
    filterErrors(valueObj)
    filterWarnings(valueObj)
  }
}

/**
 *
 * @param valueObj
 * @param rowObj
 * @param templateField
 */
function updateReadOnlyState(valueObj, rowObj, templateField, templateFields) {
  // get the predecessors
  const listGovernance = getListGovernance(templateField) || {}
  const metadata = listGovernance.meta || {}
  const predecessors = metadata.predecessors || []

  // determine if predecessors have been filled
  const hasUnmetPredecessors = predecessors.reduce((memo, fieldId) => {
    const valueHolder = rowObj[fieldId]
    let value = (valueHolder || {}).cell_value
    value = templateFields[fieldId].default_value ? templateFields[fieldId].default_value : value
    return memo || !value
  }, false)

  // for now, whether it's readonly or not is solely based on unmet predecessors
  valueObj.metadata = valueObj.metadata || {}
  valueObj.metadata.readOnly = hasUnmetPredecessors
}

/**
 *
 * @param valueObj
 * @param rowObj
 * @param templateField
 */
function updateValidOptions(valueObj, rowObj, templateField, templateFields) {
  // get the valid rows
  const validOptions = getPossibleValues(rowObj, templateField, templateFields)

  // we set the valid rows to the value metadata, which will be synced to the cell
  valueObj.metadata.validOptions = validOptions
}

/**
 *
 * @param rowObj
 * @param templateField
 * @returns {...any[]}
 */
function getPossibleValues(rowObj, templateField, templateFields) {
  // get all valid rows for this list
  const listGovernance = getListGovernance(templateField)
  const listColumnId = listGovernance.list_column_id
  const listData = ListDataManager.getListData(listGovernance.list_id)
  const listRows = listData.rowsWithColumnIds
  // generate the query to execute against the rows
  const query = generateQuery(rowObj, templateField, templateFields)
  const uniqueValues = queryForValues(
    query,
    listColumnId,
    listRows,
    listGovernance.lists.updated_at,
  )
  return uniqueValues
}

/**
 *
 * @type {Function}
 */

const queryForValues = memoize(queryForValuesRaw, (query, listColumnId, listRows, updated_at) => {
  const keys = Object.keys(query)
    .map(key => `[${key}:${query[key]}]`)
    .join('_!_')
  const memoKey = `${listColumnId}+${keys}+${updated_at}`
  return memoKey
})

/**
 *
 * @param query
 * @param listColumnId
 * @param listRows
 * @returns {...any[]}
 */
function queryForValuesRaw(query, listColumnId, listRows) {
  const queryKeys = Object.keys(query)
  // filter the list rows by the dependencies and values
  const validRows = Object.values(listRows).filter(row => {
    return queryKeys.reduce((memo, queryKey) => {
      return (
        memo &&
        query[queryKey] &&
        (typeof query[queryKey] === 'string'
          ? query[queryKey] == row[queryKey]
          : query[queryKey].includes(row[queryKey]))
      )
    }, true)
  })

  // get the distinct values of the column from the results
  const uniqueValues = validRows.reduce((memo, result) => {
    const value = result[listColumnId]
    memo.add(value)
    return memo
  }, new Set())

  // convert to array
  const uniqueValuesArr = [...uniqueValues]

  // sort the unique values
  uniqueValuesArr.sort()

  // return things after they are sorted
  return uniqueValuesArr
}

/**
 *
 * @param rowObj
 * @param templateField
 * @returns {{}}
 */
function generateQuery(rowObj, templateField, templateFields) {
  // get all valid rows for this list
  const listGovernance = getListGovernance(templateField)
  const columnsInList = listGovernance.meta.allColumnsInList
  const { predecessors } = listGovernance.meta
  const delimiterHolder = predecessors[0] ? templateFields[predecessors[0]] : templateField

  const multi =
    delimiterHolder.field.list_governance && !!delimiterHolder.field.list_governance.multi
  const delimiter = multi ? delimiterHolder.field.list_governance.delimiter : null
  // for each of the deps, get the values from the table
  const valuesForDeps = predecessors.reduce((memo, depFieldId) => {
    const valueHolder = rowObj[depFieldId]
    // TODO: Split into multiple values
    let value = (valueHolder || {}).cell_value
    if (multi && value) {
      value = value.split(delimiter)
    }
    // magic sauce for resubmits. If the default value has changed, use that to overwrite what was originally submitted.
    memo[depFieldId] = templateFields[depFieldId].default_value
      ? templateFields[depFieldId].default_value
      : value
    return memo
  }, {})

  // create a list of field ids (dep) to list_column_id
  const depToListColId = columnsInList.reduce((memo, col) => {
    const listGov = getListGovernance(col)
    memo[col.field_id] = listGov.list_column_id
    return memo
  }, {})

  // create a query object for the rows
  const query = Object.keys(valuesForDeps).reduce((memo, fieldId) => {
    const listColumndId = depToListColId[fieldId]
    memo[listColumndId] = valuesForDeps[fieldId]
    return memo
  }, {})

  return query
}

/**
 * Gets the list_governance for the template_field depending on where
 * it's supposed to come from (variable or not)
 *
 * @param templateField
 * @returns
 */
function getListGovernance(templateField) {
  const gov = templateField.field.segment_type === 'variable' ? templateField : templateField.field

  return gov.list_governance
}

/**
 *
 * @param valueObj
 * @param templateField
 */
function handleInvalidValues(valueObj, templateField, context) {
  const listGovernance = getListGovernance(templateField)
  const predecessors = listGovernance.meta.predecessors || []
  const trigger = context.trigger || {}

  // if there are no predecessors, it wasn't a chain, so do nothing
  if (predecessors.length === 0) {
    return
  }

  // if this is a previously successfully submitted value, it was valid once, so don't clear it on resubmit so we can show a warning.
  // problematic. we need to check if any of the dependencies above it have changed.
  if (
    context.isResubmit &&
    context.originalValue &&
    context.parentOriginalValue == context.parentCurrentValue &&
    valueObj.cell_value == valueObj.originalValue
  ) {
    return
  }

  // if this field is the one that caused the change, we don't want to
  // clear the value.  We want to keep it so we can show an error
  // note: this is purposefully ==.  Don't change it to ===, the server
  // varies with whether it returns a string or integer.
  if (trigger.fieldId == templateField.field_id) {
    return
  }

  const columnsInList = listGovernance.meta.allColumnsInList
  const multi =
    columnsInList[0].field.list_governance && !!columnsInList[0].field.list_governance.multi
  const delimiter = multi ? columnsInList[0].field.list_governance.delimiter : null

  // extract the necessary parts
  const value = valueObj.cell_value
  if (!value) {
    return
  }

  const { validOptions } = valueObj.metadata
  const values = valueObj.cell_value.split(delimiter)
  const newValues = []

  values.forEach(val => {
    if (validOptions.includes(val)) {
      newValues.push(val)
    }
  })

  // If there are values that are valid we want to apply them to the cell value.
  // If there's only one valid option, set that value. Sets dependent field values.
  // NO LONGER VALID: If there are no valid options in the cell value currently, and valid options is
  // greater than 1, set cell value to null.

  if (newValues.length) {
    valueObj.cell_value = newValues.join(delimiter)
  } else if (validOptions.length === 1) {
    autofillIfSingleItem(valueObj, templateField, context)
  } else {
    // We have disabled setting the cell_value to null if there are no valid options;
    // this is because we want the user to see the previous invalid value.
    // valueObj.cell_value = null
  }

  if (!valueObj.cell_value) {
    filterErrors(valueObj)
    filterWarnings(valueObj)
  }
}

/**
 *
 * @param handsOnTable
 * @param row
 * @param columnDataId
 * @param dropdownItems
 */
function autofillIfSingleItem(valueObj, templateField, context) {
  // get the predecessors
  const listGovernance = getListGovernance(templateField)
  const predecessors = listGovernance.meta.predecessors || []

  // if there are no predecessors, it wasn't a chain, so do nothing
  if (predecessors.length === 0) {
    return
  }

  // if this is a previously successfully submitted value, it was valid once, so don't clear it on resubmit so we can show a warning.
  if (
    context.isResubmit &&
    context.originalValue &&
    context.parentOriginalValue == context.parentCurrentValue &&
    valueObj.cell_value == valueObj.originalValue
  ) {
    return
  }

  // extract the necessary parts
  const { validOptions } = valueObj.metadata

  // if there is only one, update it
  if (validOptions.length === 1) {
    valueObj.cell_value = validOptions[0]

    // filter out the warnings if we just autofilled it because it shouldn't be there anymore.
    filterWarnings(valueObj)

    // flag that it was auto populated, not by the user
    valueObj.metadata.autoPopulated = true
  }
}

function clearAllOriginalValuesOnAnyListFieldChangee(
  valueObj,
  templateField,
  context,
  dataContainer,
) {
  const listGovernance = getListGovernance(templateField)
  // remove all the original valuse for all columsn in this list on any change to any of the columns in the list.
  listGovernance.meta.allColumnsInList.forEach(lc => {
    dataContainer.data[valueObj.metadata.row][lc.field_id].originalValue = null
  })
}

function filterErrors(valueObj = {}) {
  if (valueObj.metadata) {
    const validationErrors = valueObj.metadata.validationErrors || []
    valueObj.metadata.validationErrors = validationErrors.filter(err => {
      return err !== 'Value must be in list'
    })
  }
}

function filterWarnings(valueObj = {}) {
  if (valueObj.metadata) {
    const warnings = valueObj.metadata.warnings || []
    valueObj.metadata.warnings = warnings.filter(err => {
      return err !== 'Value is no longer in list'
    })
  }
}
